import api from '../../../services/api';

export interface StockValuationSummary {
  goodsPLN: number;
  landedPLN: number;
  freightPLN: number;
  extraPLN: number;
  appliedPLN: number;
  receivedExtraPLN: number;
  unreceivedExtraPLN: number;
  remainingValuePLN: number | null;
  valuationComplete: boolean;
  /** Common split unit for freight/landed costs (pcs/m2/m3); null when unresolved. */
  allocationUnit?: string | null;
}
export interface StockValuation extends StockValuationSummary {
  version: string;
  issues: string[];
  lines: {
    id: string;
    name: string;
    unit: string;
    ordered: number;
    received: number;
    remaining: number;
    extraPLN: number;
    freightPLN?: number;
    landedPLN?: number;
  }[];
  transports: {
    id: string;
    label: string;
    originalAmount: number | null;
    currency: string;
    /** Share of the whole truck derived from quantities (system-owned). */
    percent: number | null;
    amountPLN: number | null;
    exchangeRate: number | null;
    costAllocationMethod?: string;
    hasCustomerOrders: boolean;
  }[];
  batches: {
    id: string;
    pzNumber: string;
    quantityReceived: number;
    quantityRemaining: number;
    unit: string;
    totalCostPLN: number;
    remainingValuePLN: number | null;
    extraTotalPLN: number;
  }[];
  history: {
    id: string;
    reason: string;
    createdAt: string;
    createdBy: string;
  }[];
}
export interface StockPurchase extends StockValuationSummary {
  id: string;
  orderNumber: string;
  manualOrderNumber?: string;
  useManualNumbering: boolean;
  supplierName: string;
}

// This endpoint requires purchase-price access and returns a complete numeric read model.
// Reject missing values rather than rendering redacted/missing money as zero.
function normalizeSummary(raw: StockValuationSummary): StockValuationSummary {
  for (const key of [
    'goodsPLN',
    'landedPLN',
    'freightPLN',
    'extraPLN',
    'appliedPLN',
    'receivedExtraPLN',
    'unreceivedExtraPLN',
    'remainingValuePLN',
  ] as const) {
    if (key === 'remainingValuePLN' && raw[key] === null) continue;
    if (typeof raw?.[key] !== 'number' || !Number.isFinite(raw[key]))
      throw new Error('Niepełna odpowiedź wyceny magazynu.');
  }
  return raw;
}
export async function fetchStockValuation(id: string): Promise<StockValuation> {
  const { data } = await api.get<StockValuation>(
    `/supplier-orders/${id}/stock-valuation`
  );
  normalizeSummary(data);
  return data;
}
export async function fetchStockPurchases(): Promise<{
  rows: StockPurchase[];
  truncated: boolean;
}> {
  const { data } = await api.get<{ rows: StockPurchase[]; truncated: boolean }>(
    '/supplier-orders/stock-purchases'
  );
  data.rows.forEach(normalizeSummary);
  return data;
}
